import React, { useState, useMemo, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import FigurService from '../services/figureService';
import { IFigureDto } from '../models/IFigureDto';

export interface SearchFieldRef {
  clearSearch: () => void;
}

interface SearchFieldProps {
  /**
   * Collection ID for scoped search
   */
  collectionId: number;
  /**
   * Callback fired when search results are available
   */
  onSearchResults?: (results: IFigureDto[], totalCount: number, hasMore: boolean) => void;
  /**
   * Callback fired when search mode changes
   */
  onSearchModeChange?: (isSearching: boolean) => void;
  /**
   * Callback fired when search starts to force grid view
   */
  onForceGridView?: () => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Debounce delay
   */
  debounceMs?: number;
  /**
   * Additional styles
   */
  sx?: object;

  useAi?: boolean;

  /**
   * Enable unified search mode (runs both customer data and AI search in parallel)
   */
  unifiedSearch?: boolean;
}

const SearchField = forwardRef<SearchFieldRef, SearchFieldProps>(
  (
    {
      collectionId,
      onSearchResults,
      onSearchModeChange,
      onForceGridView,
      placeholder = 'Søk i samling',
      debounceMs = 300,
      sx = {},
      useAi = false,
      unifiedSearch = false,
    },
    ref,
  ) => {
    const [value, setValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchMode, setIsSearchMode] = useState(false);
    const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null);

    const isSearchEnabled = isSearchMode && searchTerm.trim().length >= 2;

    // Unified search: run both queries in parallel
    const unifiedQueries = useQueries({
      queries: [
        {
          queryKey: ['search-results-customer', searchTerm, collectionId],
          queryFn: async () => {
            console.log(`SearchField: Customer data search "${searchTerm}" collection ${collectionId}`);
            return await FigurService.searchCustomerData(searchTerm, collectionId, 1, 50);
          },
          enabled: isSearchEnabled && unifiedSearch,
          staleTime: 2 * 60 * 1000,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
        {
          queryKey: ['search-results-ai', searchTerm, collectionId],
          queryFn: async () => {
            console.log(`SearchField: AI search "${searchTerm}" collection ${collectionId}`);
            return await FigurService.aiSearchImages(searchTerm, collectionId, 1, 50);
          },
          enabled: isSearchEnabled && unifiedSearch,
          staleTime: 2 * 60 * 1000,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      ],
    });

    // Single search query (for non-unified mode)
    const singleSearchQuery = useInfiniteQuery({
      queryKey: ['search-results', searchTerm, collectionId, useAi],
      queryFn: async ({ pageParam = 1 }) => {
        console.log(`SearchField: Searching "${searchTerm}" page ${pageParam} collection ${collectionId}`);
        const result = useAi
          ? await FigurService.aiSearchImages(searchTerm, collectionId, pageParam, 50)
          : await FigurService.searchCustomerData(searchTerm, collectionId, pageParam, 50);
        return result;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.hasMore && lastPage.nextCursor ? parseInt(lastPage.nextCursor) : undefined;
      },
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: isSearchEnabled && !unifiedSearch,
    });

    // Merge unified search results
    const unifiedResults = useMemo(() => {
      if (!unifiedSearch) return { results: [], totalCount: 0, hasMore: false, isLoading: false };

      const [customerQuery, aiQuery] = unifiedQueries;
      const isLoading = customerQuery.isLoading || aiQuery.isLoading;

      // Get results from both queries (handle errors gracefully)
      const customerResults = customerQuery.data?.data || [];
      const aiResults = aiQuery.data?.data || [];
      const customerTotal = customerQuery.data?.totalCount || 0;
      const aiTotal = aiQuery.data?.totalCount || 0;

      // Merge results by imageId, ensuring customer data results appear first
      // Create a Set of customer result imageIds for fast duplicate checking
      const customerImageIds = new Set(customerResults.map((figure) => figure.imageId));

      // Start with customer data results (they always come first)
      const mergedResults: IFigureDto[] = [...customerResults];

      // Append AI results that aren't already in customer results
      aiResults.forEach((figure) => {
        if (!customerImageIds.has(figure.imageId)) {
          mergedResults.push(figure);
        }
      });
      const uniqueCount = mergedResults.length;
      const combinedTotal = customerTotal + aiTotal - (customerResults.length + aiResults.length - uniqueCount);
      const hasMore = customerQuery.data?.hasMore || false || aiQuery.data?.hasMore || false;

      return {
        results: mergedResults,
        totalCount: combinedTotal,
        hasMore,
        isLoading,
      };
    }, [unifiedSearch, unifiedQueries]);

    // Single search results
    const { data: singleData, hasNextPage: singleHasNextPage } = singleSearchQuery;
    const singleResults = useMemo(() => singleData?.pages.flatMap((page) => page.data) || [], [singleData]);
    const singleTotalCount = singleData?.pages[0]?.totalCount || 0;

    // Determine which results to use
    const searchResults = unifiedSearch ? unifiedResults.results : singleResults;
    const totalCount = unifiedSearch ? unifiedResults.totalCount : singleTotalCount;
    const hasMore = unifiedSearch ? unifiedResults.hasMore : singleHasNextPage || false;

    // Notify parent of search mode changes
    useEffect(() => {
      if (onSearchModeChange) {
        onSearchModeChange(isSearchMode);
      }
      // Force grid view as soon as search mode starts
      if (isSearchMode && onForceGridView) {
        onForceGridView();
      }
    }, [isSearchMode, onSearchModeChange, onForceGridView]);

    // Notify parent of search results
    useEffect(() => {
      if (isSearchMode && onSearchResults) {
        onSearchResults(searchResults, totalCount, hasMore);
      }
    }, [isSearchMode, searchResults, totalCount, hasMore, onSearchResults]);

    // Create debounced function for potential future use (not used for search execution)
    useEffect(() => {
      debouncedSearchRef.current = debounce(() => {
        // Reserved for future use
      }, debounceMs);
      return () => {
        if (debouncedSearchRef.current) {
          debouncedSearchRef.current.cancel();
        }
      };
    }, [debounceMs]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);

      // If field is emptied, immediately clear search and return to normal view
      if (newValue.trim().length === 0) {
        setSearchTerm('');
        setIsSearchMode(false);
        // Cancel any pending debounced calls
        if (debouncedSearchRef.current) {
          debouncedSearchRef.current.cancel();
        }
      }
    };

    const handleSearch = useCallback(() => {
      const trimmedTerm = value.trim();

      if (trimmedTerm.length >= 2) {
        setSearchTerm(trimmedTerm);
        setIsSearchMode(true);
      } else {
        // Clear search if term is too short
        setSearchTerm('');
        setIsSearchMode(false);
      }
    }, [value]);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    };

    // Expose clear search function
    const clearSearch = useCallback(() => {
      setValue('');
      setSearchTerm('');
      setIsSearchMode(false);
      // Cancel any pending debounced calls
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    }, []);

    useImperativeHandle(ref, () => ({
      clearSearch,
    }));

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'white',
          p: 0,
          ...sx,
        }}>
        <TextField
          fullWidth
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          size="small"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end" aria-label="search" sx={{ p: 0.5 }}>
                    <SearchIcon sx={{ color: 'neutral.400' }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                '& .MuiOutlinedInput-input': {
                  paddingRight: '14px',
                },
              },
            },
          }}
        />
      </Paper>
    );
  },
);

export default SearchField;
